import React from "react";
import { useNavigate } from "react-router-dom";
import { HiChevronLeft as HiChevronLeftSolid } from "react-icons/hi";
import {
  HiSun,
  HiMoon,
  HiComputerDesktop,
} from "react-icons/hi2";
import Header from "../../components/layout/Header";
import { useThemeStore, ThemePreference } from "../../stores/themeStore";

const options: {
  value: ThemePreference;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    value: "light",
    label: "Light",
    description: "Always use light theme",
    icon: HiSun,
  },
  {
    value: "dark",
    label: "Dark",
    description: "Always use dark theme",
    icon: HiMoon,
  },
  {
    value: "system",
    label: "System",
    description: "Follow your device settings",
    icon: HiComputerDesktop,
  },
];

const AppearanceSettings: React.FC = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeStore();

  return (
    <div className="w-full pt-16">
      <Header />
      <div className="container max-w-2xl mx-auto py-8 px-8 flex flex-col">
        {/* Header with Back Button */}
        <div className="flex items-center pb-4">
          <button
            onClick={() => navigate("/settings")}
            className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
          >
            <HiChevronLeftSolid className="h-6 w-6 text-base-content" />
          </button>
          <h1 className="text-3xl font-semibold text-base-content ml-4">
            Appearance
          </h1>
        </div>

        {/* Theme Options */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 divide-y divide-base-300 mt-4">
          {options.map((option) => {
            const Icon = option.icon;
            const isSelected = theme === option.value;

            return (
              <button
                key={option.value}
                onClick={() => setTheme(option.value)}
                className="w-full px-6 py-5 flex items-center justify-between hover:bg-base-200 transition-colors duration-200 text-left focus:outline-none"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isSelected
                        ? "bg-primary/15 text-primary"
                        : "bg-base-200 text-base-content/50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-base-content font-heading">
                      {option.label}
                    </p>
                    <p className="text-sm text-base-content/60">
                      {option.description}
                    </p>
                  </div>
                </div>

                {/* Radio indicator */}
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? "border-primary"
                      : "border-base-content/30"
                  }`}
                >
                  {isSelected && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Info text */}
        <p className="text-xs text-base-content/40 mt-4 px-2">
          Your preference is saved automatically and persists across sessions.
        </p>
      </div>
    </div>
  );
};

export default AppearanceSettings;
